import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { pickRealStore } from "@/lib/merchantStore";
import {
  ELIGIBLE_SUBSCRIPTION_STATUSES,
  pickFallbackSubscription,
  subscriptionFallbackIds,
  subscriptionWatchIds,
} from "@/lib/subscriptionLookup";

interface DomainGating {
  isLoading: boolean;
  currentPlan: string;
  customDomainAllowed: boolean;
  domainsAllowed: number; // -1 = unlimited
  domainsUsed: number;
  canAddDomain: boolean;
  remainingDomains: number | null; // null = unlimited
  storeId: string | null;
  isTrial: boolean;
}

const PLAN_DEFAULTS: Record<string, { custom_domain: boolean; domains_allowed: number }> = {
  free: { custom_domain: false, domains_allowed: 0 },
  basic: { custom_domain: true, domains_allowed: 1 },
  pro: { custom_domain: true, domains_allowed: 5 },
  business: { custom_domain: true, domains_allowed: -1 },
};

export const useDomainFeatureGating = () => {
  const [gating, setGating] = useState<DomainGating>({
    isLoading: true,
    currentPlan: "free",
    customDomainAllowed: false,
    domainsAllowed: 0,
    domainsUsed: 0,
    canAddDomain: false,
    remainingDomains: 0,
    storeId: null,
    isTrial: false,
  });
  const [watchIds, setWatchIds] = useState<string[]>([]);

  const load = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setGating(prev => ({ ...prev, isLoading: false }));
        return;
      }

      // Real store (pickRealStore), never the empty auto-created "my-store" row
      const [settingsRes, storesRes, domainsRes] = await Promise.all([
        supabase.from("store_settings").select("id, created_at").eq("user_id", user.id),
        supabase.from("stores").select("id, created_at").eq("owner_id", user.id),
        supabase
          .from("domains")
          .select("id", { count: "exact" })
          .eq("user_id", user.id),
      ]);

      const storeId = pickRealStore(settingsRes.data, storesRes.data)?.row.id;
      const domainsUsed = domainsRes.count ?? 0;
      // Double key: the merchant's own other ids (store_settings / stores rows of this user only).
      const fallbackIds = subscriptionFallbackIds(storeId, settingsRes.data, storesRes.data);
      setWatchIds(subscriptionWatchIds(storeId, fallbackIds));

      let planSlug = "free";
      let featureLimits: any = null;
      let isTrial = false;

      if (storeId) {
        // 1) Current lookup by the resolved id — unchanged.
        let { data: sub } = await supabase
          .from("store_subscriptions")
          .select("*, plans(*)")
          .eq("store_id", storeId)
          .in("status", [...ELIGIBLE_SUBSCRIPTION_STATUSES])
          .limit(1)
          .maybeSingle();

        // 2) Fallback on the merchant's own other ids only when nothing was found.
        if (!sub && fallbackIds.length > 0) {
          const { data: rows } = await supabase
            .from("store_subscriptions")
            .select("*, plans(*)")
            .in("store_id", fallbackIds)
            .in("status", [...ELIGIBLE_SUBSCRIPTION_STATUSES]);
          sub = pickFallbackSubscription(rows as any[], fallbackIds) as any;
        }

        if (sub?.plans) {
          const plan = sub.plans as any;
          planSlug = plan.slug || "free";
          featureLimits = plan.feature_limits;
          isTrial = sub.status === "trial";
        }
      }

      // Use feature_limits from DB if available, otherwise use defaults
      const defaults = PLAN_DEFAULTS[planSlug] || PLAN_DEFAULTS.free;
      const customDomainAllowed = featureLimits?.custom_domain ?? defaults.custom_domain;
      const domainsAllowed = featureLimits?.domains_allowed ?? defaults.domains_allowed;

      const isUnlimited = domainsAllowed === -1;
      const canAddDomain = customDomainAllowed && (isUnlimited || domainsUsed < domainsAllowed);

      setGating({
        isLoading: false,
        currentPlan: planSlug,
        customDomainAllowed,
        domainsAllowed,
        domainsUsed,
        canAddDomain,
        remainingDomains: isUnlimited ? null : Math.max(0, domainsAllowed - domainsUsed),
        storeId: storeId || null,
        isTrial,
      });
    } catch (error) {
      console.error("Domain feature gating error:", error);
      // Safe fallback: allow existing access, don't block
      setGating(prev => ({ ...prev, isLoading: false, canAddDomain: true, customDomainAllowed: true }));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Realtime: auto-refresh when subscription changes — covers both keys.
  const watchKey = watchIds.join(",");
  useEffect(() => {
    if (!watchKey) return;

    const channel = supabase
      .channel(`domain_gating_${watchKey}_${Math.random().toString(36).slice(2)}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'store_subscriptions',
          filter: `store_id=in.(${watchKey})`,
        },
        () => {
          console.log('[useDomainFeatureGating] Subscription changed, refreshing gating...');
          load();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [watchKey, load]);

  return { ...gating, refresh: load };
};
